import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useTemplateSettings } from "@/features/material-compliance/api/templateSettingsApi"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Navigate, useLocation, useNavigate } from "react-router"
import { SettingsPage } from "../../../components/SettingsPage"
import { useTemplateSettingsSave } from "../../../hooks/useTemplateSettingsSave"
import {
  SUPPLIER_INQUIRY_TAB_TITLE_KEYS,
  SUPPLIER_INQUIRY_TABS,
  supplierInquiriesTabPath,
  type SupplierInquiryTab,
} from "../../../paths"
import { pdfFooterNeedsSetup } from "../../../setup/setupChecks"
import { AttachmentTab } from "./AttachmentTab"
import { DeadlinesTab } from "./DeadlinesTab"
import { EmailsTab } from "./EmailsTab"
import { SupplierReceivesSummary } from "./SupplierReceivesSummary"

/** How the org's supplier inquiries are written, followed up and attached.
 *  Each tab has its own URL; all three stay mounted, so switching tabs keeps
 *  unsaved edits. */
export function SupplierInquiriesPage() {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  // One saver for every Save button on the page, see useTemplateSettingsSave.
  const saver = useTemplateSettingsSave()
  // The tab with something left to set up is marked, like the page in the nav.
  const { data: settings } = useTemplateSettings()
  const tabNeedsSetup: Record<SupplierInquiryTab, boolean> = {
    emails: false,
    deadlines: false,
    attachment: !!settings && pdfFooterNeedsSetup(settings),
  }
  const tab = SUPPLIER_INQUIRY_TABS.find(
    (candidate) => pathname === supplierInquiriesTabPath(candidate),
  )
  if (!tab) return <Navigate to={supplierInquiriesTabPath("emails")} replace />

  return (
    <SettingsPage
      title={translate("SETTINGS/SUPPLIER_INQUIRIES/TITLE")}
      description={translate("SETTINGS/SUPPLIER_INQUIRIES/DESCRIPTION")}
      notice={translate("SETTINGS/GENERAL/NOTICE")}
      width="wide"
    >
      {/* Together, apart from the page's divided sections: the summary sits
          above the tabs it gathers from. */}
      <div className="flex flex-col gap-6">
        <SupplierReceivesSummary />
        <Tabs
          value={tab}
          // replace: switching tabs should not fill the back button.
          onValueChange={(next) =>
            navigate(supplierInquiriesTabPath(next as SupplierInquiryTab), {
              replace: true,
            })
          }
          className="flex flex-col gap-6"
        >
          <TabsList className="self-start">
            {SUPPLIER_INQUIRY_TABS.map((value) => (
              <TabsTrigger key={value} value={value} className="gap-1.5">
                {translate(SUPPLIER_INQUIRY_TAB_TITLE_KEYS[value])}
                {tabNeedsSetup[value] && (
                  <>
                    <span
                      aria-hidden
                      className="size-1.5 rounded-full bg-warning"
                    />
                    <span className="sr-only">
                      {translate("SETTINGS/SETUP/NEEDS_SETUP")}
                    </span>
                  </>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent
            value="emails"
            forceMount
            className="data-[state=inactive]:hidden"
          >
            <EmailsTab saver={saver} />
          </TabsContent>
          <TabsContent
            value="deadlines"
            forceMount
            className="data-[state=inactive]:hidden"
          >
            <DeadlinesTab saver={saver} />
          </TabsContent>
          <TabsContent
            value="attachment"
            forceMount
            className="data-[state=inactive]:hidden"
          >
            <AttachmentTab saver={saver} />
          </TabsContent>
        </Tabs>
      </div>
    </SettingsPage>
  )
}
